import { paymentFormSchema } from '../../config/accountingForm.schemas';
import React, { useEffect, useState } from 'react';
import DataTable from '../../../../shared/components/patterns/DataTable';
import ConfirmActionDialog from '../../../../shared/components/patterns/ConfirmActionDialog';
import AccountingAttachmentsPanel from '../shared/AccountingAttachmentsPanel.jsx';
import IncomeDetailDialog from './IncomeDetailDialog.jsx';
import { AccountingInlineActions, getPaymentsTableColumns } from '../shared/accountingPage.config';

function getClientResolutionHint(status) {
  if (status === 'legacy_fallback') return 'Cliente da snapshot legacy';
  if (status === 'missing_client_id') return 'ClientId mancante';
  if (status === 'unresolved_client_reference') return 'ClientId non risolto nel catalogo clienti';
  return '';
}

export default function PaymentsTable({
  payments,
  allPayments,
  invoices = [],
  clients,
  onRegisterPayment,
  onReversePayment,
  onRestorePayment,
  onCancelPayment,
  onUpdatePayment,
  canManageAccountingDestructiveActions = false,
  focusedEntityId = '',
  focusedMode = 'view',
  onClearFocus,
  views = [],
  activeViewId,
  onViewChange,
}) {
  const [registerRequest, setRegisterRequest] = useState(null);
  const [restoreRequest, setRestoreRequest] = useState(null);
  const [reverseRequest, setReverseRequest] = useState(null);
  const [detailPayment, setDetailPayment] = useState(null);
  const currentDetailPayment =
    payments.find((item) => String(item.id) === String(detailPayment?.id)) || detailPayment;
  const [detailMode, setDetailMode] = useState('view');
  const columns = getPaymentsTableColumns().map((column) => {
    if (column.id !== 'clientId') return column;

    return {
      ...column,
      render: (payment) => {
        const clientName = payment.clientResolvedName || payment.client || 'Cliente non risolto';
        const hint = getClientResolutionHint(payment.clientResolutionStatus);
        return (
          <div>
            <p className="accounting-table-primary">{clientName}</p>
            {hint ? (
              <p className="accounting-table-secondary accounting-table-secondary--warning">{hint}</p>
            ) : null}
          </div>
        );
      },
    };
  });
  useEffect(() => {
    if (!focusedEntityId) return;
    const target = payments.find((item) => String(item.id) === String(focusedEntityId));
    if (!target) return;
    setDetailPayment(target);
    setDetailMode(focusedMode);
  }, [focusedEntityId, focusedMode, payments]);

  function getActions(payment) {
    const actions = [
      {
        id: 'preview',
        label: 'Anteprima incasso',
        iconKey: 'actions.preview',
        onClick: () => {
          setDetailMode('view');
          setDetailPayment(payment);
        },
      },
      {
        id: 'edit',
        label: 'Modifica',
        title: 'Modifica incasso',
        iconKey: 'actions.edit',
        onClick: () => {
          setDetailPayment(payment);
          setDetailMode('edit');
        },
      },
    ];

    if (payment.status === 'incassato') {
      if (canManageAccountingDestructiveActions) {
        actions.push({
          id: 'reverse',
          label: 'Storna',
          title: 'Storna incasso',
          iconKey: 'actions.reset',
          onClick: () => setReverseRequest(payment),
        });
      }
      return actions;
    }

    if (payment.status === 'stornato' && canManageAccountingDestructiveActions && onRestorePayment) {
      actions.push({ id: 'restore', label: 'Ripristina incasso', iconKey: 'actions.reset',
        onClick: () => setRestoreRequest(payment) });
    }

    if (payment.status !== 'stornato' && payment.status !== 'annullato') {
      actions.unshift({
        id: 'register',
        label: 'Registra incasso',
        title: 'Registra incasso',
        iconKey: 'actions.accept',
        tone: 'success',
        onClick: () => setRegisterRequest(payment),
      });
      if (canManageAccountingDestructiveActions && onCancelPayment) {
        actions.push({ id: 'cancel', label: 'Annulla incasso', iconKey: 'actions.reject',
          tone: 'danger', onClick: () => onCancelPayment(payment) });
      }
    }

    return actions;
  }

  return (
    <>
      <section className="accounting-table-section">
        {!views.length ? (
          <header className="accounting-section-heading">
            <h2>Registro Incassi</h2>
            <p>{payments.length} incassi trovati</p>
          </header>
        ) : null}
        <DataTable
          ariaLabel="Registro Incassi"
          formSchema={paymentFormSchema}
          views={views}
          activeViewId={activeViewId}
          onViewChange={onViewChange}
          columns={[
            ...columns,
            {
              id: 'actions',
              header: '',
              render: (payment) => {
                const clientName = payment.clientResolvedName || payment.client || 'Cliente non risolto';
                return (
                  <AccountingInlineActions
                    actions={getActions(payment)}
                    extra={
                      <AccountingAttachmentsPanel
                        entity={payment}
                        linkedEntityType="ACCOUNTING_PAYMENT"
                        entityLabel={payment.invoiceNumber || `incasso ${payment.id}`}
                        clientName={clientName}
                      />
                    }
                  />
                );
              },
            },
          ]}
          rows={payments}
          getRowId={(payment) => payment.id}
          onRowClick={(item) => {
            setDetailPayment(item);
            setDetailMode('view');
          }}
          emptyState="Nessun incasso trovato con i filtri selezionati"
        />
      </section>
      <IncomeDetailDialog
        open={Boolean(detailPayment)}
        mode="payment"
        payment={currentDetailPayment}
        planPayments={allPayments}
        invoice={invoices.find(item => String(item.id) === String(currentDetailPayment?.invoiceId))}
        clients={clients}
        linkedPayments={payments.filter(
          (payment) =>
            detailPayment?.invoiceId != null && String(payment.invoiceId) === String(detailPayment.invoiceId)
        )}
        onClose={() => {
          setDetailPayment(null);
          onClearFocus?.();
        }}
        focusComments={focusedMode === 'comments'}
        initialMode={detailMode}
        onRegisterPayment={(payment) => setRegisterRequest(payment)}
        onUpdatePayment={onUpdatePayment}
        onRestorePayment={canManageAccountingDestructiveActions && onRestorePayment ? (item) => setRestoreRequest(item) : undefined}
      />
      <ConfirmActionDialog
        open={Boolean(registerRequest)}
        title="Registrare questo incasso?"
        description={`Confermi la registrazione dell'incasso ${registerRequest?.invoiceNumber || ''}?`}
        confirmLabel="Registra incasso"
        cancelLabel="Annulla"
        onCancel={() => setRegisterRequest(null)}
        onConfirm={async () => {
          await onRegisterPayment?.(registerRequest.id);
          setRegisterRequest(null);
        }}
      />
      <ConfirmActionDialog
        open={Boolean(restoreRequest)}
        title="Ripristinare questo incasso?"
        description="L’incasso tornerà da incassare e potrai registrarlo nuovamente."
        confirmLabel="Ripristina incasso"
        cancelLabel="Indietro"
        onCancel={() => setRestoreRequest(null)}
        onConfirm={async () => {
          const result = await onRestorePayment?.(restoreRequest.id);
          if (result?.ok !== false) setRestoreRequest(null);
        }}
      />
      <ConfirmActionDialog
        open={Boolean(reverseRequest)}
        title="Stornare questo incasso?"
        description={`L'incasso ${reverseRequest?.invoiceNumber || ''} verrà stornato.`}
        confirmLabel="Storna incasso"
        cancelLabel="Indietro"
        tone="danger"
        onCancel={() => setReverseRequest(null)}
        onConfirm={async () => {
          await onReversePayment?.(reverseRequest.id);
          setReverseRequest(null);
        }}
      />
    </>
  );
}
